import { Box, Button, Grid, GridProps, Tooltip } from "@mui/material";
import { forwardRef, MouseEventHandler, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import IconWrapper from "../../common/components/functional/icons/IconWrapper";
import { ReactComponent as MessagingGoBackIcon } from "../../common/icons/messaging-go-back.svg";
import { BORDER } from "./constants";
import { ToolbarAction } from "./types/common";

type Props = {
  inputComponent?: ReactNode;
  actions: ToolbarAction[];
  GridProps?: GridProps;
  InputGridProps?: GridProps;
  compact?: boolean;
  onBack?: () => void;
};

const goBackIconSx = {
  fontSize: 22,
  color: "text.secondary",
  "& path": { stroke: "currentColor", strokeOpacity: 1, fill: "none" },
} as const;

const actionCellSx = (actionSize: number, borderSide: "left" | "right") => ({
  flex: `0 0 ${actionSize}px`,
  width: actionSize,
  minWidth: actionSize,
  maxWidth: actionSize,
  height: "100%",
  ...(borderSide === "left" ? { borderLeft: BORDER } : { borderRight: BORDER }),
  boxSizing: "border-box" as const,
});

const actionButtonSx = {
  width: "100%",
  height: "100%",
  minWidth: 0,
  padding: 0,
  borderRadius: 0,
} as const;

const TooltipButton = ({
  title,
  disabled = false,
  onClick,
  children,
}: {
  title: string;
  disabled?: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
  children: ReactNode;
}) => (
  <Tooltip title={title} enterDelay={1500}>
    <Box
      component="span"
      sx={{ display: "flex", width: "100%", height: "100%" }}
    >
      <Button
        disabled={disabled}
        aria-label={title}
        sx={actionButtonSx}
        onClick={onClick}
      >
        {children}
      </Button>
    </Box>
  </Tooltip>
);

const MessagingToolbar = forwardRef<HTMLDivElement, Props>(
  (
    {
      inputComponent,
      actions,
      GridProps = {},
      InputGridProps = {},
      compact = false,
      onBack,
    }: Props,
    ref,
  ) => {
    const { t } = useTranslation("common");
    const height = compact ? 48 : 56;
    const actionSize = compact ? 48 : 64;

    return (
      <Grid
        ref={ref}
        {...GridProps}
        sx={{
          display: "flex",
          width: "100%",
          height,
          flexShrink: 0,
          borderBottom: BORDER,
          boxSizing: compact ? "content-box" : "border-box",
        }}
      >
        {onBack && (
          <Grid item sx={actionCellSx(actionSize, "right")}>
            <TooltipButton title={t("back")} onClick={onBack}>
              <IconWrapper icon={MessagingGoBackIcon} sx={goBackIconSx} />
            </TooltipButton>
          </Grid>
        )}
        <Grid
          item
          xs
          alignContent="center"
          paddingLeft="16px"
          minWidth={0}
          overflow="hidden"
          {...InputGridProps}
        >
          {inputComponent || null}
        </Grid>
        {actions.map(
          ({ onClick, icon, disabled = false, ariaLabel }, index) => (
            <Grid key={index} item sx={actionCellSx(actionSize, "left")}>
              {ariaLabel ? (
                <TooltipButton
                  title={ariaLabel}
                  disabled={disabled}
                  onClick={onClick}
                >
                  {icon}
                </TooltipButton>
              ) : (
                <Button
                  disabled={disabled}
                  sx={actionButtonSx}
                  onClick={onClick}
                >
                  {icon}
                </Button>
              )}
            </Grid>
          ),
        )}
      </Grid>
    );
  },
);

MessagingToolbar.displayName = "MessagingToolbar";
export default MessagingToolbar;
